import { Component, OnInit, Inject, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { Observable, Subject, combineLatest } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { SetChapters } from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryState } from 'src/app/story/state/story.state';
import { StoryModel } from 'src/app/story/models/story.model';
import { takeUntil } from 'rxjs/operators';
import { SetStoryText } from 'src/app/story/state/story.actions';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { SetBookmarkText } from 'src/app/bookmarks/state/bookmarks.actions';

export interface ContentDialogData {
  chapterId?: string;
}

@Component({
  selector: 'app-content-dialog',
  templateUrl: './content-dialog.component.html',
  styleUrls: ['./content-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentDialogComponent implements OnInit, OnDestroy {
  @Select(StoryChaptersState.getStoryChapters)
  chapters$: Observable<ChapterModel>;

  chapters: ChapterModel[];
  selectedChapter: ChapterModel;

  bookmarkForm: FormGroup;
  chapterForm: FormGroup;

  private ngUnsubscribe = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ContentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: ContentDialogData,
    private store: Store
  ) { }

  ngOnInit() {
    // Initialize chapter form
    this.chapterForm =  this.formBuilder.group({
      displayName: ['', Validators.maxLength(60)],
      description: ['', Validators.maxLength(2000)]
    });

    // Initialize bookmark form
    this.bookmarkForm =  this.formBuilder.group({
      displayName: ['', [Validators.maxLength(60), Validators.required]]
    });

    // Initialize chapters
    this.chapters = this.store.selectSnapshot(
      StoryChaptersState.getStoryChapters
    );

    // Select a starting chapter
    if (this.data.chapterId) {
      this.selectChapter(this.data.chapterId);
    } else {
      this.selectChapter(this.chapters[0].id);
    }

    // Load bookmark once on open
    const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
    this.setBookmarkFormValues(bookmark);

    // Handle chapter form changes
    this.chapterForm.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.handleChapterFormChanges();
      });

    // Handle bookmark form changes
    this.bookmarkForm.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.handleBookmarkFormChanges();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  
  handleChapterClick(chapter: ChapterModel) {
    this.selectChapter(chapter.id);
  }

  handleChapterFormChanges() {
    const chapterIndex = this.chapters.findIndex(_chapter => _chapter.id === this.selectedChapter.id);
    this.chapters[chapterIndex].info.description = this.chapterForm.get('description').value;
    this.chapters[chapterIndex].info.displayName = this.chapterForm.get('displayName').value;

    this.store.dispatch(new SetChapters(this.chapters));
  }

  handleBookmarkFormChanges() {
    const bookmark = this.store.selectSnapshot(BookmarksState.getSelectedBookmark);
    const story = this.store.selectSnapshot(StoryState.getStory);
    const displayName = this.bookmarkForm.get('displayName').value;
    this.store.dispatch(new SetStoryText(displayName));
    this.store.dispatch(new SetBookmarkText(displayName));
    
  }

  selectChapter(chapterId: string) {
    this.selectedChapter = this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
      .find(_chapter => _chapter.id === chapterId);

    if (this.selectedChapter) {
      this.setChapterFormValues(this.selectedChapter);
    }
  }

  setChapterFormValues(chapter: ChapterModel) {
    this.chapterForm.setValue({
      'displayName': chapter.info.displayName,
      'description': chapter.info.description
    });
  }

  setBookmarkFormValues(bookmark: BookmarkModel) {
    this.bookmarkForm.setValue({
      displayName: bookmark.title
    });
  }

  closeDialog() {
    this.dialogRef.close(this.chapters);
  }

  // return css block for image
  getImageStyle(chapter): any {
    const thumbnails = this.store.selectSnapshot(StoryChaptersState.getRegisteredThumbnails);
    const thumbnail = thumbnails.get(chapter.id);

    // use snapshot if present, otherwise use small
    if (thumbnail) {
      const url = (thumbnail.snapshot) ? thumbnail.snapshot : thumbnail.urls.small;
      return {
        'background-image': `url(${url})`,
        'background-size': 'cover'
      };
    } else {
      return {};
    }

  }
}
